import Graph from 'graphology';

import type { GraphDto } from '../api/graph.js';

import { DEFAULT_EDGE_COLOR, EDGE_COLORS } from './graph-constants.js';
import {
  STRUCTURAL_KINDS,
  buildClusterCenters,
  buildParentChildIndex,
  randomFallbackPosition,
  seedChildPosition,
  seedStructuralPositions,
  type SeedPosition,
} from './graph-layout-seed.js';
import { usesCurvedEdges } from './graph-layout-settings.js';
import {
  buildNodeAttributes,
  type SigmaEdgeAttributes,
  type SigmaNodeAttributes,
} from './graph-node-attributes.js';

export type { SigmaEdgeAttributes, SigmaNodeAttributes } from './graph-node-attributes.js';

export interface AdaptOptions {
  /** Pre-computed Leiden community membership (Phase G.1 may pass this). */
  readonly communityMemberships?: ReadonlyMap<string, number>;
}

/**
 * Convert a GraphDto into a graphology graph ready for Sigma.js. Seeds an
 * initial layout (radial structural ring + parent-near-child) so ForceAtlas2
 * converges quickly even on 47k+ node repos.
 */
export function buildGraphologyGraph(
  dto: GraphDto,
  opts: AdaptOptions = {},
): Graph<SigmaNodeAttributes, SigmaEdgeAttributes> {
  const graph = new Graph<SigmaNodeAttributes, SigmaEdgeAttributes>();
  const nodeCount = dto.nodes.length;
  const structuralSpread = Math.sqrt(Math.max(nodeCount, 1)) * 40;
  const childJitter = Math.sqrt(Math.max(nodeCount, 1)) * 3;

  const { parentToChildren } = buildParentChildIndex(dto);
  const nodeIndex = new Map(dto.nodes.map((n) => [n.id, n]));
  const structural = dto.nodes.filter((n) => STRUCTURAL_KINDS.has(n.kind));
  const clusters = buildClusterCenters(opts.communityMemberships, structuralSpread, nodeCount);
  const positions = seedStructuralPositions(structural, structuralSpread);

  for (const node of structural) {
    const pos = positions.get(node.id)!;
    const community = opts.communityMemberships?.get(node.id);
    addNode(graph, node, pos, nodeCount, community);
  }

  const visited = new Set<string>(structural.map((n) => n.id));
  const queue: string[] = [...visited];
  while (queue.length > 0) {
    const head = queue.shift()!;
    const kids = parentToChildren.get(head) ?? [];
    for (const childId of kids) {
      if (visited.has(childId)) continue;
      visited.add(childId);
      const child = nodeIndex.get(childId);
      if (!child) continue;
      const community = opts.communityMemberships?.get(childId);
      const pos = seedChildPosition({
        child,
        parentPos: positions.get(head),
        clusters,
        community,
        childJitter,
      });
      positions.set(childId, pos);
      addNode(graph, child, pos, nodeCount, community);
      queue.push(childId);
    }
  }

  for (const node of dto.nodes) {
    if (graph.hasNode(node.id)) continue;
    const community = opts.communityMemberships?.get(node.id);
    const pos = randomFallbackPosition(structuralSpread);
    positions.set(node.id, pos);
    addNode(graph, node, pos, nodeCount, community);
  }

  appendEdges(graph, dto, nodeCount);
  return graph;
}

function addNode(
  graph: Graph<SigmaNodeAttributes, SigmaEdgeAttributes>,
  node: GraphDto['nodes'][number],
  pos: SeedPosition,
  nodeCount: number,
  community: number | undefined,
): void {
  graph.addNode(
    node.id,
    buildNodeAttributes({
      id: node.id,
      kind: node.kind,
      name: node.name,
      ...(node.filePath ? { filePath: node.filePath } : {}),
      ...(node.startLine !== undefined ? { startLine: node.startLine } : {}),
      x: pos.x,
      y: pos.y,
      nodeCount,
      ...(community !== undefined ? { community } : {}),
    }),
  );
}

function appendEdges(
  graph: Graph<SigmaNodeAttributes, SigmaEdgeAttributes>,
  dto: GraphDto,
  nodeCount: number,
): void {
  const edgeBaseSize = nodeCount > 5_000 ? 0.3 : nodeCount > 1_000 ? 0.5 : 1.0;
  const curved = usesCurvedEdges(nodeCount);
  for (const edge of dto.edges) {
    if (!graph.hasNode(edge.source) || !graph.hasNode(edge.target)) continue;
    if (graph.hasEdge(edge.source, edge.target)) continue;
    const color = EDGE_COLORS[edge.type] ?? DEFAULT_EDGE_COLOR;
    graph.addEdge(edge.source, edge.target, {
      size: edgeBaseSize,
      color,
      relationType: edge.type,
      ...(curved ? { type: 'curved', curvature: 0.12 + Math.random() * 0.08 } : { type: 'line' }),
    });
  }
}
